import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { useTranslation } from 'react-i18next';
import { Camera, ChevronLeft, ChevronRight, ImageOff, Trash2 } from 'lucide-react';
import { CATALOG_MAX_PRODUCT_PHOTOS, type Product, type ProductPhoto } from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
} from '@recipes/ui';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { preparePhoto } from '@/lib/images/prepare-photo';
import { offlineDb } from '@/lib/offline/db';
import { queryKeys } from '@/lib/query-keys';
import { addProductPhoto, patchProduct } from './catalog-queue';
import { useProductThumb } from './hooks';

/** What the list, the scan sheet and the product card know about a product's picture. */
export interface ProductPictureRef {
  id: string;
  name: string;
  brand?: string | null;
  thumbnailUrl?: string | null;
}

const label = (product: ProductPictureRef) =>
  [product.brand, product.name].filter(Boolean).join(' ');

/**
 * The product's picture as a button: what the package looks like, at a glance (which label was
 * that wine?). Opens the large view. Renders nothing when the product has no picture, unless
 * `placeholder` asks for the empty frame.
 */
export function ProductThumbButton({
  product,
  className,
  placeholder,
}: {
  product: ProductPictureRef;
  className?: string;
  placeholder?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const thumb = useProductThumb(product.id, product.thumbnailUrl);
  if (!thumb)
    return placeholder ? (
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-md border bg-muted',
          className,
        )}
      >
        <ImageOff className="size-5 text-muted-foreground" aria-hidden />
      </span>
    ) : null;
  return (
    <>
      <button
        type="button"
        className={cn(
          'shrink-0 overflow-hidden rounded-md border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
        onClick={() => setOpen(true)}
        aria-label={t('catalog.photos.enlarge', { name: label(product) })}
        data-testid="product-thumb"
      >
        <img src={thumb} alt="" className="size-full object-cover" loading="lazy" />
      </button>
      {open && <ProductPhotoDialog product={product} open onOpenChange={setOpen} />}
    </>
  );
}

/**
 * The pictures of a product, large. Signed URLs in the device copy may have run out, so the
 * dialog asks for fresh ones when there is a connection; without one it shows the small picture
 * the device keeps.
 */
export function ProductPhotoDialog({
  product,
  open,
  onOpenChange,
  startAt = 0,
}: {
  product: ProductPictureRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startAt?: number;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [index, setIndex] = useState(startAt);
  const thumb = useProductThumb(product.id, product.thumbnailUrl);
  const fresh = useQuery({
    queryKey: queryKeys.catalog.product(product.id),
    queryFn: () => api.products.get(product.id),
    enabled: open && online,
    staleTime: 60_000,
    retry: false,
  });
  const photos: ProductPhoto[] = fresh.data?.photos ?? [];
  const count = Math.max(photos.length, 1);
  const current = photos[Math.min(index, count - 1)];
  // The first picture is the one the device keeps; any other needs the connection.
  const src = current?.url ?? current?.thumbnailUrl ?? (index === 0 ? thumb : null);
  const step = (by: number) => setIndex((i) => (i + by + count) % count);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="product-photo-dialog"
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') step(-1);
          if (e.key === 'ArrowRight') step(1);
        }}
      >
        <DialogHeader>
          <DialogTitle>{label(product)}</DialogTitle>
          <DialogDescription>
            {count > 1
              ? t('catalog.photos.position', { index: Math.min(index, count - 1) + 1, count })
              : current?.role === 'LABEL'
                ? t('catalog.photos.role.LABEL')
                : t('catalog.photos.role.FRONT')}
          </DialogDescription>
        </DialogHeader>
        <div className="relative flex min-h-[40vh] items-center justify-center rounded-md bg-muted">
          {src ? (
            <img
              src={src}
              alt={t('catalog.photos.alt', { name: label(product) })}
              className="max-h-[70vh] w-full rounded-md object-contain"
              data-testid="product-photo-large"
            />
          ) : (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {online ? t('app.loading') : t('catalog.photos.offline')}
            </p>
          )}
          {count > 1 && (
            <>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute left-2 top-1/2 size-11 -translate-y-1/2"
                onClick={() => step(-1)}
                aria-label={t('catalog.photos.previous')}
              >
                <ChevronLeft aria-hidden />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute right-2 top-1/2 size-11 -translate-y-1/2"
                onClick={() => step(1)}
                aria-label={t('catalog.photos.next')}
              >
                <ChevronRight aria-hidden />
              </Button>
            </>
          )}
        </div>
        {!online && photos.length === 0 && src && (
          <p className="text-xs text-muted-foreground">{t('catalog.photos.offlineSmall')}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The pictures on the product card: every photo small, the large view on tap, and – for
 * products the household may change – adding a photo (front or label) and removing one.
 * Both work without a connection; a new photo waits in the catalog queue.
 */
export function ProductGallery({
  product,
  onChanged,
}: {
  product: Product;
  onChanged?: (product: Product) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const first = useProductThumb(product.id, product.photos[0]?.thumbnailUrl);
  // Photos taken here that the server does not have yet.
  const waiting = useLiveQuery(
    async () =>
      (await offlineDb.pendingCatalog.where('entityId').equals(product.id).toArray()).reduce(
        (sum, action) => sum + (action.photos ?? []).filter((p) => !p.assetId).length,
        0,
      ),
    [product.id],
    0,
  );
  const [failed, setFailed] = useState<Set<string>>(new Set());
  useEffect(() => setFailed(new Set()), [product.id, product.updatedAt]);
  const queryClient = useQueryClient();
  // Pages that show the product from the API (the product page, the large view) follow at once.
  const publish = (next: Product) => {
    queryClient.setQueryData(queryKeys.catalog.product(product.id), next);
    onChanged?.(next);
  };

  // A global product is never changed itself: the change creates the household's own copy.
  const mayChange = product.editable || product.scope === 'GLOBAL';
  const canAdd = mayChange && product.photos.length + waiting < CATALOG_MAX_PRODUCT_PHOTOS && !busy;

  const add = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const blob = await preparePhoto(file);
      await addProductPhoto(product, {
        file: blob,
        name: file.name,
        role: product.photos.some((p) => p.role === 'FRONT') ? 'LABEL' : 'FRONT',
      });
      const stored = await offlineDb.catalogProducts.get(product.id);
      if (stored) publish(stored.product);
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (assetId: string) => {
    setBusy(true);
    try {
      const saved = await patchProduct(product, {
        photos: product.photos
          .filter((p) => p.assetId !== assetId)
          .map((p) => ({ assetId: p.assetId, role: p.role })),
      });
      if (product.photos[0]?.assetId === assetId) await offlineDb.catalogThumbs.delete(product.id);
      publish(saved);
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };

  if (product.photos.length === 0 && waiting === 0 && !mayChange) return null;

  return (
    <section aria-label={t('catalog.photos.title')} data-testid="product-gallery">
      <h3 className="mb-1 text-sm font-semibold">{t('catalog.photos.title')}</h3>
      <ul className="flex flex-wrap gap-2">
        {product.photos.map((photo, index) => {
          const src = index === 0 ? first : failed.has(photo.assetId) ? null : photo.thumbnailUrl;
          return (
            <li key={photo.assetId} className="relative">
              <button
                type="button"
                className="flex size-24 items-center justify-center overflow-hidden rounded-md border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setViewing(index)}
                aria-label={t('catalog.photos.enlargeNth', {
                  index: index + 1,
                  role: t(`catalog.photos.role.${photo.role}`),
                })}
                data-testid="product-gallery-photo"
              >
                {src ? (
                  <img
                    src={src}
                    alt=""
                    className="size-full object-cover"
                    loading="lazy"
                    onError={() => setFailed((prev) => new Set(prev).add(photo.assetId))}
                  />
                ) : (
                  <ImageOff className="size-6 text-muted-foreground" aria-hidden />
                )}
              </button>
              <span className="pointer-events-none absolute bottom-1 left-1 rounded bg-background/80 px-1 text-[10px]">
                {t(`catalog.photos.role.${photo.role}`)}
              </span>
              {mayChange && (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute -right-2 -top-2 size-7 rounded-full shadow"
                  disabled={busy}
                  onClick={() => void remove(photo.assetId)}
                  aria-label={t('catalog.photos.remove', { index: index + 1 })}
                  data-testid="product-gallery-remove"
                >
                  <Trash2 className="size-3.5" aria-hidden />
                </Button>
              )}
            </li>
          );
        })}
        {waiting > 0 && (
          <li
            className="flex size-24 items-center justify-center rounded-md border border-dashed p-2 text-center text-xs text-muted-foreground"
            data-testid="product-gallery-waiting"
          >
            {t('catalog.photos.waiting', { count: waiting })}
          </li>
        )}
        {canAdd && (
          <li>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              tabIndex={-1}
              aria-hidden
              onChange={(e) => void add(e.target.files?.[0])}
              data-testid="product-gallery-input"
            />
            <button
              type="button"
              className="flex size-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed text-xs text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => inputRef.current?.click()}
              data-testid="product-gallery-add"
            >
              <Camera className="size-5" aria-hidden />
              {t('catalog.photos.add')}
            </button>
          </li>
        )}
      </ul>
      {viewing !== null && (
        <ProductPhotoDialog
          product={{
            id: product.id,
            name: product.name,
            brand: product.brand,
            thumbnailUrl: product.photos[0]?.thumbnailUrl,
          }}
          open
          startAt={viewing}
          onOpenChange={(next) => !next && setViewing(null)}
        />
      )}
    </section>
  );
}
